import React, {useState} from 'react';
import {connect} from 'react-redux';
import {Link, useHistory} from 'react-router-dom';
import {compose} from 'redux';
import * as Yup from 'yup';
import {get, post} from '../../../api/api';
import {
  uriConfirmActivationCode,
  uriCreateUser,
  uriRequestActivationCode,
} from '../../../api/endpoints';

import {
  AppForm,
  AppFormField,
  AppFormSubmitButton,
} from '../../../components/forms';
import Header2 from '../../../components/headers/header2';
import {Column, Row} from '../../../components/layout';
import {addToGlobalStyles, addToWizard} from '../../../store/actions';
import {strings} from '../../../util/strings';
import {censorEmail, censorPhone} from '../../../util/utility';
import AppErrorMessage from '../../../components/app-error-message';
import NewErrorMessage from '../../../components/new-error-message';
import {Icons} from '../../../util/icons';
import colors from '../../../util/colors';
import './styles.scss';
import { useFormikContext } from 'formik';

interface EnterCodeCMDProps {
  wizard: any;
  addToWizard(value: any): void;
  valid: boolean;
  setValid(value: any): void;
  labels: typeof strings.pt.registoCMD;
  messageLabels: typeof strings.pt.messages;
}

function EnterCodeCMD({
  addToWizard,
  wizard,
  valid,
  setValid,
  labels,
  messageLabels,
}: EnterCodeCMDProps) {
  const formik = useFormikContext<any>();
  const [guid, setGuid] = useState(
    (wizard &&
      wizard.activateRegistration &&
      wizard.activateRegistration.guid) ||
      ''
  );

  const validationSchema = Yup.object().shape({
    code: Yup.string().required(messageLabels?.requiredField),
  });
  const [submitting, setSubmitting] = useState(false);
  const [invalidCode, setInvalidCode] = useState(false);
  const [alreadyExisting, setAlreadyExisting] = useState(false);
  const [invalidCredentials, setInvalidCredentials] = useState(false);
  const [hoverErrorIcons, setHoverErrorIcons] = useState(false);
  const [showResendConfirmation, setShowResendConfirmation] = useState(false);
  const [disableResend, setDisableResend] = useState(false);

  const history = useHistory();

  const featureName = 'Register-EnterCodeCMD';

  const {selectedContact, selectedContactType} = wizard.dataValidation;

  const requestActivationCode = async () => {
    const guid = wizard.dataValidation?.currentUser?.sessionId;

    setDisableResend(true);
    try {
      await post(
        uriRequestActivationCode(),
        JSON.stringify({
          nif: wizard.insuranceValidation.nif,
          senderType: selectedContactType,
          sessionId: guid,
          name: wizard?.user?.fullName,
        }),
        false,
        featureName
      );
      addToWizard({activateRegistration: {guid}});
      setInvalidCode(false);
      setShowResendConfirmation(true);
      setGuid(guid);
    } catch (e: any) {
    } finally {
      setDisableResend(false);
    }
  };

  const defineOnMouseFunctions = (
    e: React.MouseEvent,
    fieldNamePar: string,
    dateInput?: boolean
  ) => {
    var elem: HTMLElement = e.target as HTMLElement;
    if (!dateInput) {
      var icon: HTMLElement = elem.nextSibling as HTMLElement;
      defineIconFunctions(icon, fieldNamePar);
    }

    if (
      dateInput &&
      (elem.tagName.includes('svg') || elem.tagName.includes('path'))
    ) {
      var icon: HTMLElement = elem as HTMLElement;
      defineIconFunctions(icon, fieldNamePar);
    }
  };

  const defineIconFunctions = (icon: HTMLElement, fieldNamePar: any) => {
    if (icon) {
      icon.onmouseover = function () {
        setHoverErrorIcons(true);
      };
      icon.onmouseout = function () {
        setHoverErrorIcons(false);
      };
    }
  };

  const confirmCode = async (code: string) => {
    return await get(
      uriConfirmActivationCode(code, guid),
      false,
      0,
      featureName
    );
  };

  return (
    <Column className="enter-code-container">
      <AppForm
        initialValues={{code: ''}}
        validationSchema={validationSchema}
        onSubmit={async (values: any, {validateForm}: any) => {
          const errors = await validateForm(values);
          if (Object.keys(errors).length > 0) {
            return;
          }
          setShowResendConfirmation(false);
          setSubmitting(true);
          setInvalidCode(false);

          try {
            const response = await confirmCode(values.code);

            const tenantAlias = process.env
              .REACT_APP_DEFAULT_TENANT_ALIAS as string;

            if (response) {
              const newUser = {...wizard.user, phoneNumber: null};
              await fetch(uriCreateUser(), {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                  X_MYADV_API_KEY: tenantAlias,
                  feature: featureName,
                },
                body: JSON.stringify(newUser),
              })
                .then((res: any) => {
                  if (!res.ok) {
                    return res.text().then((text: any) => {
                      if (text?.includes('UserAlreadyExisting')) {
                        setAlreadyExisting(true);
                      }
                      if (text?.includes('30003')) {
                        setInvalidCredentials(true);
                      }
                    });
                  } else {
                    history.push('/registo-completo');
                    return res.json();
                  }
                })
                .catch((e: any) => {
                  if (e.status === 500) {
                    formik.setFieldError(
                      'email',
                      labels?.registoCMD_emailPassword_existingUsername
                    );
                  }
                });
            } else {
              setInvalidCode(true);
              formik.setFieldValue('code', '');
            }
          } catch (e: any) {
          } finally {
            setSubmitting(false);
          }
        }}>
        <Header2 className="enter-code-header">{`${
          labels?.registoCMD_activation_subTitle
        } ${
          selectedContactType === 'SMS'
            ? censorPhone(selectedContact)
            : selectedContactType === 'EMAIL'
            ? censorEmail(selectedContact)
            : ''
        }`}</Header2>
        <Column
          name="enter-code-code-validation"
          className="enter-code-code-validation"
          style={{position: 'relative'}}>
          <AppFormField
            name="code"
            className="enter-code-form-field"
            placeholder={
              labels?.registoCMd_activation_activationCodePlaceholder
            }
            onMouseOver={(e) => {
              defineOnMouseFunctions(e, 'code');
            }}
          />
          {formik?.submitCount > 0 && formik.errors && (
            <AppErrorMessage
              message={
                (formik.errors.code as string) ||
                messageLabels?.requiredField
              }
              className="insurance-validation-pop-up-error-message"
            />
          )}
          {invalidCode && (
            <NewErrorMessage
              iconName={Icons.ALERT_PROBLEM}
              colorIcon={colors.alertRed}
              text={labels?.registoCMD_wrongActivationCode}></NewErrorMessage>
          )}
          {alreadyExisting && (
            <NewErrorMessage
              iconName={Icons.ALERT_PROBLEM}
              colorIcon={colors.alertRed}
              text={
                labels?.registoCMD_usernameAlreadyRegistered
              }></NewErrorMessage>
          )}
          {invalidCredentials && (
            <NewErrorMessage
              iconName={Icons.ALERT_PROBLEM}
              colorIcon={colors.alertRed}
              text={labels?.registoCMD_invalidCredentials}></NewErrorMessage>
          )}
        </Column>
        {showResendConfirmation && (
          <Row className="app-center enter-code-resend-code-message">
            <Header2 className="enter-code-header">{`${labels?.registoCMD_changePassword_codeSent}`}</Header2>
          </Row>
        )}
        <Row className="app-center enter-code-resend-code">
          <Link
            className={
              disableResend
                ? 'activate-registration-anchors-disable'
                : 'activate-registration-anchors'
            }
            to={history.location.pathname + history.location.search}
            onClick={async () =>
              !disableResend && (await requestActivationCode())
            }>
            {labels?.registoCMD_activation_sendNewCode}
          </Link>
        </Row>
        <Row className="app-center">
          <AppFormSubmitButton
            title={labels?.registoCMD_activation_buttonText}
            disabled={submitting}
            className="stepper-continue-button enter-code-continue-button"
          />
        </Row>
      </AppForm>
    </Column>
  );
}

const mapStateToProps = ({strings, wizard, user}: any) => ({
  labels: strings.value.pt.registoCMD,
  messageLabels: strings.value.pt.messages,
  wizard: wizard.value,
  user: user.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToGlobalStyles: (value: any) => dispatch(addToGlobalStyles(value)),
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps)(EnterCodeCMD)
);

//export default EnterCode;
